// AI findings (PLAN §10.2, §7.1; task 32): a findings narrative drafted by AI from a saved report or a Case — key
// Actors, patterns, anomalies, open questions — in which every sentence cites the claims it rests on. The person edits
// it (a small, safe subset of Markdown: paragraphs, bullet lists, **bold**, *italic*, and citation markers `[C3]`)
// and signs it off. These are the shapes the API, the AI package and the editor share, and the pure rules that split
// the text into sentences, find the ones without a source, and say what a reader must know first.

import { isCaseHeading } from './case-file';
import type { ChatCaveat, ClaimCitation } from './chat';
import { type CompareRoute, ROUTE_LABELS } from './compare';
import { EXECUTIVE_SUMMARY_KEY } from './dossier';
import { DOSSIER_LABELS } from './dossier-labels';
import type { FindingsTranslationView, ReportLanguage } from './report-language';
import type { Sensitivity } from './uploads';

// ── Sources, sections, limits ────────────────────────────────────────────────────────────────────────────────

/** What findings are drawn from: a saved report (its matching entities) or a Case (what it groups and links). */
export const FINDINGS_SOURCE_KINDS = ['report', 'case'] as const;
export type FindingsSourceKind = (typeof FINDINGS_SOURCE_KINDS)[number];
export const isFindingsSourceKind = (v: unknown): v is FindingsSourceKind => FINDINGS_SOURCE_KINDS.includes(v as FindingsSourceKind);

export const FINDINGS_LIMITS = {
  title: 200,
  sections: 20,
  sectionTitle: 120,
  /** Characters of one section's text. */
  body: 20_000,
  /** Entities whose claims the AI reads, and claims per entity. */
  entities: 30,
  claimsPerEntity: 30,
  /** Characters of the analyst's notes for a redraft (the whole draft, and each section's). */
  notes: 2000,
} as const;

/** The analyst's notes a draft was steered with (Redraft with notes): for every section, and per section key. */
export interface FindingsNotes {
  notes: string;
  sections: Record<string, string>;
}

/**
 * A redraft request: `notes` for the whole draft, `sectionNotes` per section key, and `only` the section keys to
 * redraft (empty or absent: every section). Unknown keys are refused; text is trimmed and kept within the limits.
 */
export function parseRedraftRequest(
  raw: unknown,
  keys: readonly string[],
): { ok: true; value: { notes: FindingsNotes; only: string[] } } | { ok: false; error: string } {
  const b = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const clean = (v: unknown) => (typeof v === 'string' ? v.trim().replace(/\r\n/g, '\n') : '');
  const notes = clean(b.notes);
  if (notes.length > FINDINGS_LIMITS.notes) return { ok: false, error: `Keep the notes under ${FINDINGS_LIMITS.notes} characters.` };
  const sections: Record<string, string> = {};
  if (b.sectionNotes !== undefined && b.sectionNotes !== null) {
    if (typeof b.sectionNotes !== 'object' || Array.isArray(b.sectionNotes))
      return { ok: false, error: 'Section notes must be keyed by section.' };
    for (const [k, v] of Object.entries(b.sectionNotes as Record<string, unknown>)) {
      if (!keys.includes(k)) return { ok: false, error: 'A note names a section these findings don’t have.' };
      const n = clean(v);
      if (n.length > FINDINGS_LIMITS.notes)
        return { ok: false, error: `Keep each section’s note under ${FINDINGS_LIMITS.notes} characters.` };
      if (n) sections[k] = n;
    }
  }
  const only = Array.isArray(b.only) ? [...new Set(b.only.filter((k): k is string => typeof k === 'string'))] : [];
  if (only.some(k => !keys.includes(k))) return { ok: false, error: 'Pick sections these findings have.' };
  return { ok: true, value: { notes: { notes, sections }, only } };
}

/** The notes one section's call is given: the whole-draft notes, then this section's own. */
export function notesForSection(n: FindingsNotes | null | undefined, key: string): string {
  if (!n) return '';
  const own = n.sections[key];
  return [n.notes, own ? `For this section: ${own}` : ''].filter(Boolean).join('\n\n');
}

/** One section of the findings: a heading, what belongs in it (for the AI and the writer), and the text. */
export interface FindingsSection {
  key: string;
  title: string;
  guidance: string;
  /** Paragraphs and `- ` bullet items, with `[C1]` markers after the statements they support. */
  body: string;
  /**
   * Who drafted this section's text (D412). Absent on findings drafted before it was recorded, and on sections nobody
   * has drafted; a person's edits keep it. The finding's own `route`/`model` is the one that drafted most sections.
   */
  route?: CompareRoute | null;
  model?: string | null;
  /** This section's text came from a fallback route (the usual one failed or gave an unusable answer). */
  isFallback?: boolean;
}

type Authored = Pick<FindingsSection, 'body' | 'route' | 'model' | 'isFallback'>;

/** Who drafted a section: the route, its model, and whether it was a fallback. */
export interface FindingsAuthor {
  route: CompareRoute;
  model: string | null;
  isFallback: boolean;
}

/** The author recorded on a drafted section, or null (no text, or drafted before authors were recorded). */
export function sectionAuthor(s: Authored): FindingsAuthor | null {
  return s.route && s.body.trim() ? { route: s.route, model: s.model ?? null, isFallback: !!s.isFallback } : null;
}

/** The section with the author recorded (a run's route, model and fallback flag). */
export const withAuthor = <S extends FindingsSection>(s: S, a: FindingsAuthor): S => ({
  ...s,
  route: a.route,
  model: a.model,
  isFallback: a.isFallback,
});

/** The section without an author (its text was emptied). */
export function withoutAuthor<S extends FindingsSection>(s: S): S {
  const { route: _route, model: _model, isFallback: _fallback, ...rest } = s;
  return rest as S;
}

/** One model that drafted some sections: how many, and whether they were fallbacks. */
export interface FindingsAuthorGroup extends FindingsAuthor {
  sections: number;
}

/** Drafted sections grouped by route and model, the one that wrote most first (ties: the usual route, then the first). */
export function authorGroups(sections: readonly Authored[]): FindingsAuthorGroup[] {
  const groups = new Map<string, FindingsAuthorGroup>();
  for (const a of sections.map(sectionAuthor)) {
    if (!a) continue;
    const key = `${a.route}|${a.model ?? ''}`;
    const g = groups.get(key);
    if (g) {
      g.sections++;
      g.isFallback &&= a.isFallback;
    } else groups.set(key, { ...a, sections: 1 });
  }
  return [...groups.values()]
    .map((g, i) => ({ g, i }))
    .sort((a, b) => b.g.sections - a.g.sections || Number(a.g.isFallback) - Number(b.g.isFallback) || a.i - b.i)
    .map(x => x.g);
}

/** The model that drafted the most sections (the finding's own route and model), or null when none is recorded. */
export const leadingAuthor = (sections: readonly Authored[]): FindingsAuthor | null => authorGroups(sections)[0] ?? null;

/** Who drafted what, for the page and the caveats: the finding's own model and the sections others drafted. */
export interface FindingsAuthorship {
  /** Sections with a recorded author. */
  total: number;
  main: { route: CompareRoute | null; model: string | null };
  /** Sections drafted by another model than `main`, one group per model, most sections first. */
  others: FindingsAuthorGroup[];
}

/** Null for findings without per-section authors (drafted before D412): show the finding's own route and model. */
export function findingsAuthorship(f: {
  route: CompareRoute | null;
  model: string | null;
  sections: readonly Authored[];
}): FindingsAuthorship | null {
  const groups = authorGroups(f.sections);
  const lead = groups[0];
  if (!lead) return null;
  const main = f.route && f.model ? { route: f.route, model: f.model } : { route: lead.route, model: lead.model };
  const others = groups.filter(g => g.route !== main.route || g.model !== main.model);
  return { total: groups.reduce((n, g) => n + g.sections, 0), main, others };
}

/**
 * The structure a findings draft has when its report has no template (PLAN §10.2: key Actors, patterns, anomalies,
 * open questions). A report made from a template drafts in the template's sections instead.
 */
export const DEFAULT_FINDINGS_SECTIONS: readonly Omit<FindingsSection, 'body'>[] = [
  {
    key: 'key_actors',
    title: 'Key actors',
    guidance: 'Who matters most here and why: their roles, positions and the links that make them central.',
  },
  {
    key: 'patterns',
    title: 'Patterns',
    guidance: 'What recurs across the actors: shared funders, common members, repeated mechanisms, timing.',
  },
  {
    key: 'anomalies',
    title: 'Anomalies',
    guidance: 'What does not fit: contradictions between sources, disputed values, unusual amounts or dates, gaps.',
  },
  {
    key: 'open_questions',
    title: 'Open questions',
    guidance: 'What the data raises but does not answer, and what to check next. Cite the claims that raise each question.',
  },
];

/** What the executive summary holds (it opens the findings of a report with two or more template sections). */
export const EXECUTIVE_SUMMARY_GUIDANCE =
  'The main findings of the whole report, five to seven points, each citing the claims the sections rest on.';

/** The executive summary section, empty, titled in the findings' language. */
export const executiveSummarySection = (lang: ReportLanguage = 'en'): FindingsSection => ({
  key: EXECUTIVE_SUMMARY_KEY,
  title: DOSSIER_LABELS[lang].executiveSummary,
  guidance: EXECUTIVE_SUMMARY_GUIDANCE,
  body: '',
});

/** French titles of the default sections (findings of a report written in French start with these). */
const DEFAULT_SECTION_TITLES_FR: Record<string, string> = {
  key_actors: 'Acteurs clés',
  patterns: 'Tendances',
  anomalies: 'Anomalies',
  open_questions: 'Questions ouvertes',
};

/** The default sections, titled in the findings' language (the guidance is for the AI and stays in English). */
export const defaultFindingsSections = (lang: ReportLanguage = 'en'): Omit<FindingsSection, 'body'>[] =>
  DEFAULT_FINDINGS_SECTIONS.map(s => (lang === 'fr' ? { ...s, title: DEFAULT_SECTION_TITLES_FR[s.key] ?? s.title } : { ...s }));

export const FINDINGS_STATUSES = ['empty', 'choose', 'draft', 'signed_off'] as const;
/** `empty`: nothing written yet · `choose`: Compare-mode drafts wait for a pick · `draft`: being edited · `signed_off`. */
export type FindingsStatus = (typeof FINDINGS_STATUSES)[number];

/** One route's draft (several side by side in Compare mode). */
export interface FindingsDraft {
  route: CompareRoute;
  label: string;
  model: string | null;
  ok: boolean;
  error: string | null;
  isFallback: boolean;
  sections: { key: string; body: string }[];
  costUsd: number;
}

export interface Finding {
  id: string;
  title: string;
  source: { kind: FindingsSourceKind; id: string; label: string };
  status: FindingsStatus;
  sections: FindingsSection[];
  /** Every claim the draft could cite (what the AI read), by ref; the text cites some of them. */
  citations: ClaimCitation[];
  /** Compare mode: each route's draft, until one is adopted. */
  drafts: FindingsDraft[] | null;
  route: CompareRoute | null;
  model: string | null;
  isFallback: boolean;
  /** Highest sensitivity of what the AI read (confidential → drafted by a Cloudflare-native model). */
  sensitivity: Sensitivity;
  /** Bumped by every change; a save or sign-off names the revision it was made on. */
  revision: number;
  /** The analyst's notes the last draft was steered with (Redraft with notes), or null. */
  draftNotes: FindingsNotes | null;
  /** The language the text is written in (a report's findings are drafted in the report's language). */
  language: ReportLanguage;
  /** Translations into the other language, each marked stale once the findings changed after it was made. */
  translations: FindingsTranslationView[];
  createdBy: string | null;
  ownerName: string | null;
  signedOff: { by: string; name: string | null; at: number; revision: number } | null;
  draftedAt: number | null;
  createdAt: number;
  updatedAt: number;
  /** The author (or an admin) edits, drafts and deletes. */
  canEdit: boolean;
  /** The author, an admin, or a reviewer. */
  canSignOff: boolean;
}

export type FindingListItem = Pick<
  Finding,
  'id' | 'title' | 'source' | 'status' | 'createdBy' | 'ownerName' | 'signedOff' | 'createdAt' | 'updatedAt' | 'sensitivity'
>;

export const FINDINGS_STATUS_LABELS: Record<FindingsStatus, string> = {
  empty: 'Not drafted',
  choose: 'Pick a draft',
  draft: 'Draft',
  signed_off: 'Signed off',
};

/** Server-sent events of one AI draft (POST /api/findings/:id/draft). */
export type FindingsEvent =
  | { type: 'status'; text: string }
  | { type: 'routes'; routes: CompareRoute[]; compare: boolean; sensitivity: Sensitivity; claims: number; entities: number }
  /** Per-section drafting: a section starts, is drafted, or fails (its text stays empty). */
  | {
      type: 'section';
      key: string;
      title: string;
      index: number;
      total: number;
      state: 'drafting' | 'done' | 'failed';
      message?: string;
    }
  | { type: 'done'; finding: Finding; failed?: { key: string; title: string; message: string }[] }
  | { type: 'error'; message: string };

// ── Citations in the text ────────────────────────────────────────────────────────────────────────────────────

/** Refs a findings text cites: claims only (`C` and a number). */
export const FINDINGS_REF = /^C[1-9]\d{0,3}$/;
/** `[C1]`, `[C1, C4]` inside the text. */
const MARKER = /\[\s*(C[1-9]\d{0,3}(?:\s*[,;]\s*C[1-9]\d{0,3})*)\s*\]/g;
const MARKER_START = /^(?:\s*\[\s*C[1-9]\d{0,3}(?:\s*[,;]\s*C[1-9]\d{0,3})*\s*\])+/;

/** The refs of the markers in a piece of text, in order, without repeats. */
export function markerRefs(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(MARKER)) for (const r of (m[1] as string).split(/\s*[,;]\s*/)) if (r && !out.includes(r)) out.push(r);
  return out;
}

/** Where a marker was dropped: the spaces before it go with it (French "texte : suite" elsewhere keeps its spaces). */
const DROPPED = '\uE000';
const tidyDropped = (text: string) => text.replace(/[ \t]*\uE000/g, '').replace(/[ \t]{2,}/g, ' ');

/** The text with only the markers' refs in `allowed` kept; a marker left with none is removed. */
export function keepMarkerRefs(text: string, allowed: ReadonlySet<string>): string {
  return tidyDropped(
    text.replace(MARKER, (_m, list: string) => {
      const refs = [...new Set(list.split(/\s*[,;]\s*/).filter(r => allowed.has(r)))];
      return refs.length ? `[${refs.join(', ')}]` : DROPPED;
    }),
  );
}

/** The text with each marker ref renamed through `map` (Redraft with notes keeps one numbering); unmapped refs leave. */
export function relabelMarkerRefs(text: string, map: ReadonlyMap<string, string>): string {
  return tidyDropped(
    text.replace(MARKER, (_m, list: string) => {
      const refs = [...new Set(list.split(/\s*[,;]\s*/).flatMap(r => (map.has(r) ? [map.get(r) as string] : [])))];
      return refs.length ? `[${refs.join(', ')}]` : DROPPED;
    }),
  );
}

/**
 * The text without its citation markers (for reading aloud, counting and exports). Only the spaces before a marker
 * leave with it, so a French space before `:` `;` `!` `?` stays.
 */
export const stripMarkers = (text: string) =>
  text
    .replace(new RegExp(`\\s*${MARKER.source}`, 'g'), '')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** A statement with its markers appended before its final punctuation: `Text [C1, C2].` */
export function withMarkers(text: string, cites: readonly string[]): string {
  const t = text.trim();
  if (!cites.length) return t;
  const marker = `[${cites.join(', ')}]`;
  const end = /([.!?؟。…]+["'”’)]*)$/.exec(t);
  return end ? `${t.slice(0, end.index).trimEnd()} ${marker}${end[1]}` : `${t} ${marker}`;
}

export interface FindingsBlock {
  kind: 'p' | 'li';
  text: string;
}

/** Paragraphs (blank lines between them) and bullet items (`- ` or `* ` at the start of a line). */
export function findingsBlocks(body: string): FindingsBlock[] {
  const out: FindingsBlock[] = [];
  let para: string[] = [];
  const flush = () => {
    const text = para.join(' ').trim();
    if (text) out.push({ kind: 'p', text });
    para = [];
  };
  for (const raw of body.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    const item = /^[-*•]\s+(.*)$/.exec(line);
    if (item) {
      flush();
      if (item[1]?.trim()) out.push({ kind: 'li', text: item[1].trim() });
    } else if (!line) flush();
    else para.push(line);
  }
  flush();
  return out;
}

export interface FindingsSentence {
  /** The sentence as written (markers and emphasis included). */
  raw: string;
  refs: string[];
}

const BOUNDARY = /([.!?؟。…]+["'”’)]*)((?:\s*\[\s*C[1-9]\d{0,3}(?:\s*[,;]\s*C[1-9]\d{0,3})*\s*\])*)(?=\s|$)/g;

/**
 * The sentences of one block. A sentence ends at `.`, `!`, `?` (and their Arabic and CJK forms) followed by a space;
 * markers written after the full stop still belong to the sentence before it.
 */
export function splitSentences(text: string): FindingsSentence[] {
  const pieces: string[] = [];
  let last = 0;
  for (const m of text.matchAll(BOUNDARY)) {
    const end = (m.index ?? 0) + m[0].length;
    pieces.push(text.slice(last, end));
    last = end;
  }
  if (last < text.length) pieces.push(text.slice(last));
  const out: FindingsSentence[] = [];
  for (const p of pieces) {
    let raw = p.trim();
    // Markers opening a piece (written after the previous sentence's full stop) go back to that sentence.
    const lead = MARKER_START.exec(raw);
    const prev = out.at(-1);
    if (lead && prev) {
      prev.raw = `${prev.raw} ${lead[0].trim()}`;
      for (const r of markerRefs(lead[0])) if (!prev.refs.includes(r)) prev.refs.push(r);
      raw = raw.slice(lead[0].length).trim();
    }
    if (!/[\p{L}\p{N}]/u.test(stripMarkers(raw))) {
      if (prev) for (const r of markerRefs(raw)) if (!prev.refs.includes(r)) prev.refs.push(r);
      continue;
    }
    out.push({ raw, refs: markerRefs(raw) });
  }
  return out;
}

export interface FindingsCheck {
  sentences: number;
  /** Sentences citing no known claim, with their section. */
  uncited: { section: string; text: string }[];
  /** Markers naming a ref the findings don't have. */
  unknownRefs: string[];
  /** Known refs cited anywhere, first-cited first. */
  cited: string[];
}

/**
 * Checks every sentence: which cite nothing (or only unknown refs), and which refs are cited. A case file's fixed
 * headings (a bold line alone, `isCaseHeading`) are not statements and are skipped.
 */
export function checkFindings(sections: readonly Pick<FindingsSection, 'title' | 'body'>[], known: ReadonlySet<string>): FindingsCheck {
  const out: FindingsCheck = { sentences: 0, uncited: [], unknownRefs: [], cited: [] };
  for (const s of sections)
    for (const b of findingsBlocks(s.body))
      for (const sentence of b.kind === 'p' && isCaseHeading(b.text) ? [] : splitSentences(b.text)) {
        out.sentences++;
        const good = sentence.refs.filter(r => known.has(r));
        for (const r of sentence.refs) if (!known.has(r) && !out.unknownRefs.includes(r)) out.unknownRefs.push(r);
        for (const r of good) if (!out.cited.includes(r)) out.cited.push(r);
        if (!good.length) out.uncited.push({ section: s.title, text: stripMarkers(sentence.raw) });
      }
  return out;
}

/** A drafted section's text from the model's paragraphs (each a list of statements with their refs). */
export function sectionBody(paragraphs: readonly (readonly { text: string; cites: readonly string[] }[])[]): string {
  return paragraphs
    .map(p =>
      p
        .map(s => withMarkers(s.text, s.cites))
        .filter(Boolean)
        .join(' '),
    )
    .filter(Boolean)
    .join('\n\n');
}

// ── Checking input ───────────────────────────────────────────────────────────────────────────────────────────

export type FindingsParse<T> = { ok: true; value: T } | { ok: false; error: string };

/** Sections from an untrusted request body: same keys as before (`allowed`), titles and text within limits. */
export function parseFindingsSections(raw: unknown, allowed?: readonly FindingsSection[]): FindingsParse<FindingsSection[]> {
  if (!Array.isArray(raw) || !raw.length) return { ok: false, error: 'The findings need at least one section.' };
  if (raw.length > FINDINGS_LIMITS.sections) return { ok: false, error: `Use at most ${FINDINGS_LIMITS.sections} sections.` };
  const out: FindingsSection[] = [];
  for (const [i, s] of raw.entries()) {
    if (!s || typeof s !== 'object' || Array.isArray(s)) return { ok: false, error: `Section ${i + 1} is not readable.` };
    const o = s as Record<string, unknown>;
    const key = typeof o.key === 'string' ? o.key : '';
    const before = allowed?.find(a => a.key === key);
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(key) || (allowed && !before))
      return { ok: false, error: `Section ${i + 1} is not one of these findings.` };
    if (out.some(x => x.key === key)) return { ok: false, error: `Section ${i + 1} appears twice.` };
    const title = typeof o.title === 'string' ? o.title.replace(/\s+/g, ' ').trim() : '';
    if (!title) return { ok: false, error: `Section ${i + 1}: give it a title.` };
    if (title.length > FINDINGS_LIMITS.sectionTitle)
      return { ok: false, error: `Section ${i + 1}: keep the title under ${FINDINGS_LIMITS.sectionTitle} characters.` };
    const body = typeof o.body === 'string' ? o.body.replace(/\r\n?/g, '\n') : '';
    if (body.length > FINDINGS_LIMITS.body)
      return { ok: false, error: `“${title}”: keep the text under ${FINDINGS_LIMITS.body.toLocaleString('en')} characters.` };
    const section: FindingsSection = { key, title, guidance: before?.guidance ?? '', body };
    // A person's edit keeps who drafted the section.
    const author = before ? sectionAuthor(before) : null;
    out.push(author && body.trim() ? withAuthor(section, author) : section);
  }
  return { ok: true, value: out };
}

export const cleanFindingsTitle = (v: unknown) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

// ── What a reader must know ──────────────────────────────────────────────────────────────────────────────────

/**
 * Caveats of the findings as they stand (PLAN §8, §10.2): computed from the claims the text cites and the sentences
 * without a source, never from what the model chose to mention.
 */
export function findingsCaveats(
  f: Pick<Finding, 'citations' | 'sensitivity' | 'isFallback' | 'route'> & {
    sections: readonly (Pick<FindingsSection, 'title' | 'body'> & Partial<FindingsSection>)[];
  },
  check = checkFindings(f.sections, new Set(f.citations.map(c => c.ref))),
  lang: ReportLanguage = 'en',
): ChatCaveat[] {
  const words = DOSSIER_LABELS[lang].caveats;
  const out: ChatCaveat[] = [];
  const cited = f.citations.filter(c => check.cited.includes(c.ref));
  const allegations = cited.filter(c => c.claimKind === 'allegation').length;
  if (allegations) out.push({ kind: 'allegation', text: words.allegation(allegations) });
  const unreviewed = cited.filter(c => c.status === 'unreviewed').length;
  if (unreviewed) out.push({ kind: 'unreviewed', text: words.unreviewed(unreviewed) });
  const disputed = cited.filter(c => c.status === 'disputed').length;
  if (disputed) out.push({ kind: 'disputed', text: words.disputed(disputed) });
  if (check.uncited.length) out.push({ kind: 'unsupported', text: words.unsupported(check.uncited.length) });
  if (f.sensitivity === 'confidential') out.push({ kind: 'confidential', text: words.confidential });
  if (f.isFallback && f.route) {
    // With per-section authors, name the route that stepped in and how many sections it wrote (D412).
    const groups = authorGroups(f.sections);
    const fell = groups.filter(g => g.isFallback);
    const count = fell.reduce((n, g) => n + g.sections, 0);
    const total = groups.reduce((n, g) => n + g.sections, 0);
    out.push({
      kind: 'fallback',
      text:
        fell.length && count < total
          ? words.fallbackSome([...new Set(fell.map(g => ROUTE_LABELS[g.route]))].join(', '), count, total)
          : words.fallback(ROUTE_LABELS[fell[0]?.route ?? f.route]),
    });
  }
  return out;
}
